import CrudActionContainer from './CrudActionContainer';
import { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';
import CampaignsController, {
  CampaignResourcePayload,
  CampaignsRetrievalParams,
} from '../controllers/CampaignsController';
import CampaignChangeManager from '../syncEngine/services/changeManagers/CampaignChangeManager';
import campaignsHandler from '../errorHandlers/campaigns-handler';
import { StoreState } from '../store-interfaces';
import {
  CampaignTracking,
  CampaignType,
  QueuedContactsType,
} from '../model/CampaignsModel';
import { dispatchCampaignPostUpdates } from './campaign-post-updates';
import modalActions from '../modules/modalsState/actions';

class CampaignsCRUDActions extends CrudActionContainer {
  setErrorStatus: string;
  resetAction: string;
  deleteLocalAction: string;
  setCampaignTrackingAction: string;
  fetchQueuedContactsAction: string;
  fetchedQueuedContactsAction: string;
  cloneSuccessAction: string;
  retrieveCampaignStatsAction: string;
  retrieveCampaignStatsSuccessAction: string;
  retrieveCampaignStatsFailAction: string;

  retrieveCampaignSchedulerStatsAction: string;
  retrieveCampaignSchedulerStatsSuccessAction: string;
  retrieveCampaignSchedulerStatsFailAction: string;
  testCampaignSchedulerAction: string;
  retrieveCampaignSearchTokenAction: string;

  constructor() {
    super('CAMPAIGNS');
    this.setErrorStatus = this.makeActionKey('SET_ERRORS_ACTION');
    this.resetAction = this.makeActionKey('RESET_ACTION');
    this.deleteLocalAction = this.makeActionKey('DELETE_LOCAL');
    this.setCampaignTrackingAction = this.makeActionKey(
      'SET_CAMPAIGN_TRACKING',
    );
    this.fetchQueuedContactsAction = this.makeActionKey(
      'FETCH_QUEUED_CONTACTS',
    );
    this.fetchedQueuedContactsAction = this.makeActionKey(
      'FETCHED_QUEUED_CONTACTS',
    );
    this.cloneSuccessAction = this.makeActionKey('CLONE_SUCCESS');

    this.retrieveCampaignStatsAction = this.makeActionKey(
      'RETRIEVE_CAMPAIGN_STATS',
    );
    this.retrieveCampaignStatsSuccessAction = this.makeActionKey(
      'RETRIEVE_CAMPAIGN_STATS_SUCCESS',
    );
    this.retrieveCampaignStatsFailAction = this.makeActionKey(
      'RETRIEVE_CAMPAIGN_STATS_FAIL',
    );

    this.retrieveCampaignSchedulerStatsAction = this.makeActionKey(
      'RETRIEVE_CAMPAIGN_SCHEDULER_STATS',
    );
    this.retrieveCampaignSchedulerStatsSuccessAction = this.makeActionKey(
      'RETRIEVE_CAMPAIGN_SCHEDULER_STATS_SUCCESS',
    );
    this.retrieveCampaignSchedulerStatsFailAction = this.makeActionKey(
      'RETRIEVE_CAMPAIGN_SCHEDULER_STATS_FAIL',
    );
    this.testCampaignSchedulerAction = this.makeActionKey(
      'TEST_CAMPAIGN_SCHEDULER',
    );
    this.retrieveCampaignSearchTokenAction = this.makeActionKey(
      'RETRIEVE_CAMPAIGN_SEARCH_TOKEN_SUCCESS',
    );
  }

  fetchQueuedContacts(
    id: number,
    schedulerId: number,
    page: number,
    perPage: number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.fetchQueuedContactsAction));
      try {
        const response = await CampaignsController.getQueuedContacts(
          id,
          schedulerId,
          page,
          perPage,
        );
        dispatch(this.fetchedQueuedContacts(response, schedulerId));
        return response;
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
      }
    };
  }

  retrieveAll(params: CampaignsRetrievalParams) {
    const {
      botId,
      pageNumber,
      pageSize,
      additionalParams,
      dripCampaign,
      withSchedulersAttributes,
      signal,
    } = params;
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.reset());
      this.clearErrorStatus(dispatch);
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const response = await CampaignsController.getAll({
          botId,
          pageNumber,
          pageSize,
          additionalParams,
          dripCampaign,
          withSchedulersAttributes,
          signal,
        });
        dispatch(this.retrievedAll(response));
        return response;
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
      }
    };
  }

  retrieve(id: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      this.clearErrorStatus(dispatch);
      try {
        const response: CampaignType = await CampaignsController.getById(id);
        dispatchCampaignPostUpdates(dispatch, response);
        dispatch(this.retrieved(response));
        return response;
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
      }
    };
  }

  clearErrorStatus = (dispatch: Dispatch<StoreState>) => {
    dispatch({
      type: this.setErrorStatus,
      errorMessages: [],
    });
  };

  delete(campaignId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.deleteAction));
      dispatch({
        type: this.deleteLocalAction,
        campaignId,
      });
      try {
        const response = await CampaignChangeManager.delete(campaignId);
        return response;
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
      }
    };
  }

  updateCampaign(campaign: CampaignType, botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        const response = await CampaignsController.putCampaign(
          { ...campaign, botId }, // BE requires botId to be passed on updates...
          campaign.id,
        );
        if (response.success) {
          dispatch(this.updateSuccess(response.campaign));
          dispatchCampaignPostUpdates(dispatch, response.campaign);
          dispatch(this.unbusy());
          return response;
        }
        return response;
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
        dispatch(this.unbusy());
        throw e;
      }
    };
  }

  update(
    campaign: CampaignType,
    campaignId: number,
    withoutRetrieveAll?: boolean,
    onUpdate?: () => void,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      this.clearErrorStatus(dispatch);
      try {
        const response = await CampaignsController.putCampaign(
          campaign,
          campaignId,
        );
        if (response.success && !withoutRetrieveAll) {
          dispatchCampaignPostUpdates(dispatch, response.campaign);
          return dispatch(
            this.retrieveAll({
              botId: campaign.botId,
              pageNumber: 1,
              pageSize: 25,
              dripCampaign: campaign.scheduleType === 'drip',
              withSchedulersAttributes: true,
            }),
          );
        }
        if (onUpdate) {
          onUpdate();
        }

        return response;
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
        throw e;
      }
    };
  }

  createCampaign(campaign: CampaignType) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));
      try {
        const response = await CampaignsController.postCampaign(campaign);
        if (response.success) {
          dispatch(this.createSuccess(response.campaign));
          dispatch(this.unbusy());
          return response.campaign;
        }
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
        throw e;
      }
    };
  }

  clone(
    campaignId: number | string,
    selectedPageIds?: number[],
    botIds?: number[],
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());
      try {
        const response = await CampaignsController.clone(
          campaignId,
          selectedPageIds,
          botIds,
        );
        if (response.campaign || response.campaigns) {
          dispatch(this.retrieved(response));
          dispatch({ type: this.cloneSuccessAction, payload: response });
        }
        if (response?.errors?.length) {
          dispatch(
            modalActions.openPrettyErrorModal({
              title: 'Failed to clone',
              errorMessage: response.errors[0],
            }),
          );
        }
        return response;
      } catch (e) {
        await campaignsHandler(dispatch, this)(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieveSearchToken(campaignId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());
      try {
        const response = await CampaignsController.getSearchToken(campaignId);

        dispatch({
          type: this.retrieveCampaignSearchTokenAction,
          payload: response,
        });

        return response;
      } catch (e) {
        await campaignsHandler(dispatch, this)(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  create(campaign: CampaignType) {
    return async (dispatch: Dispatch<StoreState>) => {
      this.clearErrorStatus(dispatch);
      dispatch(this.busy(this.createAction));
      try {
        const response = await CampaignsController.postCampaign(campaign);
        if (response.success) {
          dispatch(
            this.retrieveAll({
              botId: campaign.botId,
              pageNumber: 1,
              pageSize: 25,
              dripCampaign: campaign.scheduleType === 'drip',
              withSchedulersAttributes: true,
            }),
          );
        }
        return response;
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
        throw e;
      }
    };
  }

  createPUDripCampaign(botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      this.clearErrorStatus(dispatch);

      try {
        return await CampaignsController.postPUDripCampaign(botId);
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
        throw e;
      }
    };
  }

  retrieveChatBlasterTracking(
    campaignId: number,
    pageNumber: number,
    pageSize: number,
    query?: string,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAction));
      try {
        const response = await CampaignsController.getTracking(
          campaignId,
          pageNumber,
          pageSize,
          query,
        );
        dispatch(this.setCampaignTracking(response));
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
      }
    };
  }

  retrieveCampaignStats(
    ...args: Parameters<typeof CampaignsController.getCampaignStats>
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await CampaignsController.getCampaignStats(...args);
        dispatch({
          type: this.retrieveCampaignStatsSuccessAction,
          payload: response,
        });
        return response;
      } catch (e) {
        dispatch({
          type: this.retrieveCampaignStatsFailAction,
          payload: e,
        });
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieveCampaignSchedulerStats(
    ...args: Parameters<typeof CampaignsController.getCampaignSchedulerStats>
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveCampaignSchedulerStatsAction));
      try {
        const response = await CampaignsController.getCampaignSchedulerStats(
          ...args,
        );
        dispatch({
          type: this.retrieveCampaignSchedulerStatsSuccessAction,
          payload: response,
        });
        return response;
      } catch (e) {
        dispatch({
          type: this.retrieveCampaignSchedulerStatsFailAction,
          payload: e,
        });
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  fetchedQueuedContacts(contacts: QueuedContactsType, schedulerId: number) {
    return {
      type: this.fetchedQueuedContactsAction,
      schedulerId,
      contacts,
    };
  }

  setCampaignTracking(trackings: CampaignTracking) {
    return {
      type: this.setCampaignTrackingAction,
      trackings,
    };
  }

  createRelatedCampaign(
    campaignId: number,
    relatedCampaignType: 'unicorn_detector',
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());
      try {
        const res = await CampaignsController.createRelatedCampaign(
          campaignId,
          relatedCampaignType,
        );

        const relatedCampaigns = res?.campaign;

        if (res?.success && relatedCampaigns?.length) {
          relatedCampaigns.forEach(campaign =>
            dispatch(this.createSuccess(campaign)),
          );
        }
        return res;
      } catch (e) {
        await campaignsHandler(dispatch, this)(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  reset() {
    return { type: this.resetAction };
  }

  testScheduler(
    campaignId: number,
    schedulerId: number,
    testNumbers: string[],
    withoutRetrieve?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.testCampaignSchedulerAction));
      try {
        const response = await CampaignsController.postCampaignTestScheduler(
          campaignId,
          schedulerId,
          testNumbers,
        );
        if (response.success && !withoutRetrieve) {
          dispatch(this.retrieve(campaignId));
        }
        return response;
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  rebalanceRoundRobin(campaignId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy());
      try {
        const response = await CampaignsController.rebalanceRoundRobin(
          campaignId,
        );
        if (response.success) {
          dispatch(this.retrieve(campaignId));
        }
        return response;
      } catch (e) {
        campaignsHandler(dispatch, this)(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }
}

const campaignsCRUDActions = new CampaignsCRUDActions();

export default campaignsCRUDActions;

export type CloneSuccessActionType = {
  type: typeof campaignsCRUDActions.clone;
  payload: CampaignResourcePayload;
};

export const useCampaignCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return {
    fetchQueuedContacts: (
      ...args: Parameters<CampaignsCRUDActions['fetchQueuedContacts']>
    ) => dispatch(campaignsCRUDActions.fetchQueuedContacts(...args)),
    clone: (...params: Parameters<CampaignsCRUDActions['clone']>) =>
      dispatch(campaignsCRUDActions.clone(...params)),
    create: (...args: Parameters<CampaignsCRUDActions['create']>) =>
      dispatch(campaignsCRUDActions.create(...args)),
    retrieveAll: (params: CampaignsRetrievalParams) =>
      dispatch(campaignsCRUDActions.retrieveAll(params)),
    retrieve: (...args: Parameters<CampaignsCRUDActions['retrieve']>) =>
      dispatch(campaignsCRUDActions.retrieve(...args)),
    delete: (...args: Parameters<CampaignsCRUDActions['delete']>) =>
      dispatch(campaignsCRUDActions.delete(...args)),
    update: (...args: Parameters<CampaignsCRUDActions['update']>) =>
      dispatch(campaignsCRUDActions.update(...args)),
    createCampaign: (
      ...args: Parameters<typeof campaignsCRUDActions.createCampaign>
    ) => dispatch(campaignsCRUDActions.createCampaign(...args)),
    updateCampaign: (
      ...args: Parameters<typeof campaignsCRUDActions.updateCampaign>
    ) => dispatch(campaignsCRUDActions.updateCampaign(...args)),
    retrieveCampaignStats: (
      ...args: Parameters<typeof campaignsCRUDActions.retrieveCampaignStats>
    ) => dispatch(campaignsCRUDActions.retrieveCampaignStats(...args)),
    retrieveCampaignSchedulerStats: (
      ...args: Parameters<
        typeof campaignsCRUDActions.retrieveCampaignSchedulerStats
      >
    ) => dispatch(campaignsCRUDActions.retrieveCampaignSchedulerStats(...args)),
    createPUDripCampaign: (
      ...args: Parameters<CampaignsCRUDActions['createPUDripCampaign']>
    ) => dispatch(campaignsCRUDActions.createPUDripCampaign(...args)),
    retrieveChatBlasterTracking: (
      ...args: Parameters<CampaignsCRUDActions['retrieveChatBlasterTracking']>
    ) => dispatch(campaignsCRUDActions.retrieveChatBlasterTracking(...args)),
    fetchedQueuedContacts: (
      ...args: Parameters<CampaignsCRUDActions['fetchedQueuedContacts']>
    ) => dispatch(campaignsCRUDActions.fetchedQueuedContacts(...args)),
    setCampaignTracking: (
      ...args: Parameters<CampaignsCRUDActions['setCampaignTracking']>
    ) => dispatch(campaignsCRUDActions.setCampaignTracking(...args)),
    reset: (...args: Parameters<CampaignsCRUDActions['reset']>) =>
      dispatch(campaignsCRUDActions.reset(...args)),
    testScheduler: (
      ...args: Parameters<CampaignsCRUDActions['testScheduler']>
    ) => dispatch(campaignsCRUDActions.testScheduler(...args)),
    retrieveCampaignSearchToken: (
      ...args: Parameters<CampaignsCRUDActions['retrieveSearchToken']>
    ) => dispatch(campaignsCRUDActions.retrieveSearchToken(...args)),
    createRelatedCampaign: (
      ...args: Parameters<CampaignsCRUDActions['createRelatedCampaign']>
    ) => dispatch(campaignsCRUDActions.createRelatedCampaign(...args)),
    rebalanceRoundRobin: (
      ...args: Parameters<CampaignsCRUDActions['rebalanceRoundRobin']>
    ) => dispatch(campaignsCRUDActions.rebalanceRoundRobin(...args)),
  };
};
